"use client";

import * as React from "react";
import Link from "next/link";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useScroll } from "@/components/ui/use-scroll";
import { useScrollDirection } from "@/components/ui/use-scroll-direction";
import { Logo } from "./logo";

const LINKS = [
  { label: "Accueil", href: "/#HeroSection" },
  { label: "Fonctionnalités", href: "/#features" },
  { label: "Notre app", href: "/#how-it-works" },
  { label: "Prix", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
] as const;

function useLockBodyScroll(locked: boolean) {
  React.useEffect(() => {
    if (!locked) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [locked]);
}

export function Header() {
  const [open, setOpen] = React.useState(false);
  const scrolled = useScroll(10);
  const { scrollDirection, lastScrollY } = useScrollDirection();

  useLockBodyScroll(open);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    globalThis.addEventListener("keydown", onKeyDown);
    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-transform duration-300",
        scrolled && "backdrop-blur-lg",
        scrollDirection === "down" && lastScrollY > 80 && "-translate-y-full",
        (scrollDirection === "up" || lastScrollY <= 80) && "translate-y-0",
      )}
    >
      <nav className="mx-auto flex h-20 w-full max-w-[1512px] items-center justify-between px-6">
        <Link href="/" className="flex items-center">
          <Logo className="w-[160px]" />
        </Link>

        <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:flex">
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-black/20 px-6 py-3 backdrop-blur-md">
            {LINKS.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="rounded-full px-4 py-2 font-display text-[14px] font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="hidden items-center md:flex">
          <Button
            asChild
            className="cursor-pointer rounded-full bg-[#6729FF] px-6 py-3 font-display text-[14px] font-medium text-white transition-transform duration-300 ease-out hover:scale-105 hover:bg-[#6729FF]"
          >
            <Link href="/sign-in">Connexion</Link>
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-white/20 bg-white/10 transition-colors hover:bg-white/20 md:hidden cursor-pointer"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label="Toggle menu"
        >
          <div className="flex h-4 w-5 flex-col items-center justify-center gap-1">
            <span
              className={cn(
                "h-0.5 w-full origin-center bg-white transition-all duration-300",
                open ? "translate-y-1.5 rotate-45" : "rotate-0",
              )}
            />
            <span
              className={cn(
                "h-0.5 w-full bg-white transition-all duration-300",
                open ? "scale-0 opacity-0" : "scale-100 opacity-100",
              )}
            />
            <span
              className={cn(
                "h-0.5 w-full origin-center bg-white transition-all duration-300",
                open ? "-translate-y-1.5 -rotate-45" : "rotate-0",
              )}
            />
          </div>
        </button>
      </nav>

      <MobileMenu open={open} onClose={() => setOpen(false)}>
        <div className="space-y-1">
          {LINKS.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block rounded-lg px-4 py-3 font-display text-[16px] font-medium text-white transition-colors hover:bg-white/10"
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 pt-6">
          <Button
            asChild
            className="w-full rounded-full bg-[#6729FF] py-3.5 font-display text-[15px] font-semibold text-white transition-transform duration-300 ease-out hover:scale-105 hover:bg-[#6729FF]"
          >
            <Link href="/sign-in" onClick={() => setOpen(false)}>
              Connexion
            </Link>
          </Button>
        </div>
      </MobileMenu>
    </header>
  );
}

function MobileMenu({
  open,
  onClose,
  children,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
      />

      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed left-0 top-0 bottom-0 z-[70] w-80 max-w-[85vw] border-r border-white/10 bg-black/95 backdrop-blur-lg transition-transform duration-300 ease-out supports-[backdrop-filter]:bg-black/90 md:hidden",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-full flex-col gap-2 p-6 pt-24">{children}</div>
      </div>
    </>,
    document.body,
  );
}
